import { useState } from 'react';
import {
  Dialog, Box, Stack, Typography, Button, Chip, IconButton, Alert, Tabs, Tab, Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useDispatch, useSelector } from 'react-redux';
import { confirmExtraction } from '../../../../redux/tatWizardSlice';
import {
  stNameFromGroups, groupLabelFromApiGroup, ZONES, filterPricedCodes, findStGroup,
} from '../constants';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';

const MONO = 'JetBrains Mono, ui-monospace, monospace';

function AccountCodeChip({ code, payment }) {
  const pmLabel = payment === 'BOTH' ? 'Both' : payment === 'COD' ? 'COD' : 'Prepaid';
  const pmColor = payment === 'BOTH' ? 'default' : payment === 'COD' ? 'warning' : 'info';
  return (
    <Chip
      size="small"
      color={pmColor}
      label={(
        <span>
          {code}
          <Box component="span" sx={{ ml: 0.75, px: 0.6, py: 0.1, borderRadius: 0.75, fontSize: 9, bgcolor: 'rgba(0,0,0,0.08)' }}>
            {pmLabel}
          </Box>
        </span>
      )}
      sx={{ fontFamily: MONO, fontSize: 10.5, height: 22 }}
    />
  );
}

// One service type's Min–Max TAT bands across the 5 zones, read from the merchant's
// ENTERED / uploaded values (flat z{n}_min / z{n}_max keys) — not the standard. Shows
// '—' when a zone is blank; the standard band renders beneath as a faint reference.
function ZoneBandGrid({ flat, std }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' }, gap: 1, mb: 2.25 }}>
      {ZONES.map((z) => {
        const min = flat?.[`z${z.id}_min`];
        const max = flat?.[`z${z.id}_max`];
        const has = min != null && min !== '' && max != null && max !== '';
        const sband = std?.[z.id] || std?.[String(z.id)];
        return (
          <Paper key={z.id} variant="outlined" sx={{ p: 1, textAlign: 'center', bgcolor: 'background.neutral' }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: 10, color: 'primary.dark', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {z.name.split('·').pop().trim()}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 15, lineHeight: 1.3, color: has ? 'text.primary' : 'text.disabled' }}>
              {has ? `${min}–${max}` : '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 9.5 }}>days</Typography>
            {sband && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: 9 }}>
                Std {sband[0]}–{sband[1]}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Box>
  );
}

// Preview window shown after AI extraction. Service types render as tabs across
// the top (like the Pricing wizard's Preview & Confirm); each tab shows that
// service type's entered zone TAT bands plus its parent → account-code coverage tree.
export default function TatExtractPreviewModal({ open, onClose }) {
  const dispatch = useDispatch();
  const agreement = useSelector((s) => s.tatWizard.agreement) || {};
  const merchantName = useSelector((s) => s.tatWizard.merchantName);
  const selectedSTs = useSelector((s) => s.tatWizard.selectedSTs);
  const modeByST = useSelector((s) => s.tatWizard.modeByST);
  const standardTat = useSelector((s) => s.tatWizard.standardTat);
  const tat = useSelector((s) => s.tatWizard.tat);
  const tatBlended = useSelector((s) => s.tatWizard.tatBlended);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const { data: groups = [] } = useGetServiceabilityGroupsQuery();

  const stTabs = (selectedSTs || [])
    .map((st) => {
      const allLinked = (findStGroup(groups, st)?.linkedCodes || []).filter((lc) => lc.isActive !== false);
      // Only couriers/account codes the merchant has pricing for (matches by
      // prozo_id, AC code or parent). Empty pricedKeys = not loaded → show all.
      const linkedCodes = filterPricedCodes(allLinked, pricedKeys);
      const parentKeys = [...new Set(linkedCodes.map((lc) => lc.parent || lc.cpAccountCode))];
      const parents = parentKeys.map((pk) => ({ parent: pk, codes: linkedCodes.filter((lc) => (lc.parent || lc.cpAccountCode) === pk) }));
      const apiGroup = findStGroup(groups, st);
      return {
        st,
        stName: stNameFromGroups(st, groups),
        stGroup: groupLabelFromApiGroup(apiGroup),
        std: standardTat?.[st] || null,
        parents,
      };
    })
    .filter((r) => r.stName && (r.std || r.parents.length > 0));

  const [active, setActive] = useState(stTabs[0]?.st || '');
  const activeKey = stTabs.some((r) => r.st === active) ? active : (stTabs[0]?.st || '');
  const cur = stTabs.find((r) => r.st === activeKey);
  const curCodes = cur ? cur.parents.reduce((a, p) => a + p.codes.length, 0) : 0;
  const curMode = cur ? (modeByST?.[cur.st] || 'courier_wise') : 'courier_wise';

  const onConfirm = () => {
    dispatch(confirmExtraction());
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth PaperProps={{ sx: { width: '94vw', height: '90vh', maxWidth: 1280, m: 0 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: '#fff' }}>
          <CheckIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>TAT Data · Preview &amp; Confirm</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
            {merchantName} · {stTabs.length} service type(s) in scope
          </Typography>
        </Box>
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={`${stTabs.length} service type${stTabs.length !== 1 ? 's' : ''} to review`}
          sx={{ fontWeight: 700 }}
        />
        <Button size="small" color="inherit" onClick={onClose}>Cancel</Button>
        <Button size="small" variant="contained" startIcon={<CheckIcon sx={{ fontSize: 16 }} />} onClick={onConfirm}>
          Confirm &amp; Close
        </Button>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Stack>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Per-service-type tabs */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Review the TAT values for each service type below. Make any final adjustments in the editor, then click <b>Confirm &amp; Close</b> to proceed to Review &amp; Validate.
            </Alert>
          </Box>

          <Tabs
            value={activeKey || false}
            onChange={(_, v) => setActive(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2, minHeight: 48, '& .MuiTab-root': { textTransform: 'none', minHeight: 48, alignItems: 'flex-start', px: 1.5 } }}
          >
            {stTabs.map((r) => (
              <Tab
                key={r.st}
                value={r.st}
                label={(
                  <Stack spacing={0.4} alignItems="flex-start">
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12, lineHeight: 1.1 }}>{r.stName}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: MONO, fontSize: 9.5, color: 'text.secondary', lineHeight: 1 }}>{r.st}</Typography>
                  </Stack>
                )}
              />
            ))}
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {!cur ? (
              <Alert severity="warning">No service types in scope — adjust the Operating Profile.</Alert>
            ) : (
              <>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 1.75 }}>
                  <Chip
                    size="small"
                    label={curMode === 'blended' ? 'Blended' : 'Courier-wise'}
                    sx={{
                      height: 20, fontSize: 10.5, fontWeight: 700,
                      bgcolor: curMode === 'blended' ? '#fff7ed' : '#eef2ff',
                      color: curMode === 'blended' ? '#9a3412' : '#3730a3',
                    }}
                  />
                  <Chip size="small" label={cur.stGroup} sx={{ height: 20, fontSize: 10.5 }} />
                  <Chip size="small" variant="outlined" label={`${cur.parents.length} parent(s) · ${curCodes} account code(s)`} sx={{ height: 20, fontSize: 10.5 }} />
                  {curMode === 'blended' && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      one TAT row fans out to all account codes under this service type
                    </Typography>
                  )}
                </Stack>

                {curMode === 'blended' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Min–Max TAT (days) per zone — one row applies to all account codes
                    </Typography>
                    <ZoneBandGrid flat={tatBlended?.[cur.st]} std={cur.std} />
                  </>
                )}

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Parent couriers &amp; account codes
                </Typography>
                {cur.parents.length === 0 ? (
                  <Alert severity="warning">No couriers mapped to this service type yet — no CSVs will be generated for it.</Alert>
                ) : (
                  <Stack spacing={1.25}>
                    {cur.parents.map((p) => (
                      <Paper key={p.parent} variant="outlined" sx={{ p: 1.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', display: 'block', mb: 0.75 }}>
                          {p.parent}
                          <Chip label={`${p.codes.length} code${p.codes.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 0.75, height: 16, fontSize: 9.5 }} />
                        </Typography>
                        {curMode !== 'blended' && (
                          <ZoneBandGrid flat={tat?.[cur.st]?.[p.parent]} std={cur.std} />
                        )}
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75}>
                          {p.codes.map((c) => <AccountCodeChip key={c.cpAccountCode} code={c.cpAccountCode} payment={c.payment} />)}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}

                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
                  View-only — TAT values stay fully editable in the editor; courier ↔ account-code mapping is managed at the CP master.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
